/**
 * View: Tiering Settings Form
 * Form for creating or editing tiering settings.
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { SuccessModal } from "@/components/ui/success-modal";
import type {
  TieringPeriodDetail,
  TieringPeriodWritePayload,
  TieringTierWritePayload,
} from "../../../domain/model/TieringSettingsModel";
import {
  CreateTieringPeriodUseCase,
  FetchTieringPeriodDetailUseCase,
  UpdateTieringPeriodUseCase,
} from "../../../domain/usecase/TieringPeriodUseCases";

type TierFormData = {
  name: string;
  pointsRangeFrom: string;
  pointsRangeTo: string;
  directPoints: string;
  dailyRedemptionLimit: string;
  birthdayRewardPoints: string;
};

type FormData = {
  period: string;
  validityPeriod: string;
  tiers: [TierFormData, TierFormData, TierFormData, TierFormData];
};

const initialTierData: TierFormData = {
  name: "",
  pointsRangeFrom: "",
  pointsRangeTo: "",
  directPoints: "",
  dailyRedemptionLimit: "",
  birthdayRewardPoints: "",
};

const initialFormData: FormData = {
  period: "",
  validityPeriod: "",
  tiers: [
    { ...initialTierData, name: "Bronze" },
    { ...initialTierData, name: "Silver" },
    { ...initialTierData, name: "Gold" },
    { ...initialTierData, name: "Platinum" },
  ],
};

const mapDetailToFormData = (detail: TieringPeriodDetail): FormData => {
  const yearStr = String(detail.year);
  const sortedTiers = [...detail.tiers].sort(
    (a, b) => a.tierLevel - b.tierLevel
  );

  const tierNames = ["Bronze", "Silver", "Gold", "Platinum"];
  const formTiers = tierNames.map((defaultName, index) => {
    const apiTier = sortedTiers[index];
    if (apiTier) {
      return {
        name: apiTier.tierName,
        pointsRangeFrom: String(apiTier.minPoints),
        pointsRangeTo:
          apiTier.maxPoints !== undefined ? String(apiTier.maxPoints) : "",
        directPoints: String(apiTier.directPointsMultiplier),
        dailyRedemptionLimit: String(apiTier.dailyRedemptionLimit),
        birthdayRewardPoints: String(apiTier.birthdayRewardPoints),
      };
    }
    return { ...initialTierData, name: defaultName };
  }) as [TierFormData, TierFormData, TierFormData, TierFormData];

  return {
    period: yearStr,
    validityPeriod: yearStr,
    tiers: formTiers,
  };
};

export const TieringSettingsFormView = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  
  usePageTitle(isEditMode ? "Edit Tiering Settings" : "Create Tiering Settings");

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loadDetailError, setLoadDetailError] = useState<string | null>(null);

  const loadDetail = useCallback(async (periodId: string) => {
    setIsLoadingDetail(true);
    setLoadDetailError(null);
    try {
      const detail = await FetchTieringPeriodDetailUseCase(periodId);
      setFormData(mapDetailToFormData(detail));
    } catch (error) {
      console.error("Failed to load tiering period detail:", error);
      setLoadDetailError(
        "Failed to load tiering period. The period may not exist or there was a network error."
      );
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      void loadDetail(id);
    }
  }, [id, isEditMode, loadDetail]);

  const handlePeriodChange = (value: string) => {
    setFormData((prev) => ({ ...prev, period: value, validityPeriod: value }));
    if (errors.period) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.period;
        return newErrors;
      });
    }
  };

  const handleTierChange = (
    tierIndex: number,
    field: keyof TierFormData,
    value: string
  ) => {
    setFormData((prev) => {
      const newTiers = [...prev.tiers] as [
        TierFormData,
        TierFormData,
        TierFormData,
        TierFormData
      ];
      newTiers[tierIndex] = { ...newTiers[tierIndex], [field]: value };
      return { ...prev, tiers: newTiers };
    });

    const errorKey = `tier${tierIndex}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.period) {
      newErrors.period = "Period is required";
    }

    formData.tiers.forEach((tier, index) => {
      if (!tier.name.trim()) {
        newErrors[`tier${index}_name`] = "Tier name is required";
      }

      if (!tier.pointsRangeFrom.trim()) {
        newErrors[`tier${index}_pointsRangeFrom`] =
          "Points range from is required";
      } else if (
        Number.isNaN(Number(tier.pointsRangeFrom)) ||
        Number(tier.pointsRangeFrom) < 0
      ) {
        newErrors[`tier${index}_pointsRangeFrom`] = "Must be a positive number";
      }

      if (!tier.pointsRangeTo.trim()) {
        newErrors[`tier${index}_pointsRangeTo`] = "Points range to is required";
      } else if (
        Number.isNaN(Number(tier.pointsRangeTo)) ||
        Number(tier.pointsRangeTo) < 0
      ) {
        newErrors[`tier${index}_pointsRangeTo`] = "Must be a positive number";
      }

      if (
        tier.pointsRangeFrom &&
        tier.pointsRangeTo &&
        Number(tier.pointsRangeFrom) >= Number(tier.pointsRangeTo)
      ) {
        newErrors[`tier${index}_pointsRangeTo`] =
          "Must be greater than 'from' value";
      }

      if (!tier.directPoints.trim()) {
        newErrors[`tier${index}_directPoints`] = "Direct points is required";
      } else if (
        Number.isNaN(Number(tier.directPoints)) ||
        Number(tier.directPoints) < 0
      ) {
        newErrors[`tier${index}_directPoints`] = "Must be a positive number";
      }

      if (!tier.dailyRedemptionLimit.trim()) {
        newErrors[`tier${index}_dailyRedemptionLimit`] =
          "Daily redemption limit is required";
      } else if (
        Number.isNaN(Number(tier.dailyRedemptionLimit)) ||
        Number(tier.dailyRedemptionLimit) < 0
      ) {
        newErrors[`tier${index}_dailyRedemptionLimit`] =
          "Must be a positive number";
      }

      if (!tier.birthdayRewardPoints.trim()) {
        newErrors[`tier${index}_birthdayRewardPoints`] =
          "Birthday reward points is required";
      } else if (
        Number.isNaN(Number(tier.birthdayRewardPoints)) ||
        Number(tier.birthdayRewardPoints) < 0
      ) {
        newErrors[`tier${index}_birthdayRewardPoints`] =
          "Must be a positive number";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildWritePayload = (): TieringPeriodWritePayload => {
    const yearNum = Number.parseInt(formData.period, 10);
    const tiers: TieringTierWritePayload[] = formData.tiers.map(
      (tier, idx) => ({
        tierLevel: idx + 1,
        tierName: tier.name,
        tierCode: tier.name.toUpperCase().replace(/\s+/g, "_"),
        minPoints: Number.parseInt(tier.pointsRangeFrom, 10) || 0,
        maxPoints: tier.pointsRangeTo
          ? Number.parseInt(tier.pointsRangeTo, 10)
          : undefined,
        directPointsMultiplier: Number.parseFloat(tier.directPoints) || 1,
        birthdayRewardPoints:
          Number.parseInt(tier.birthdayRewardPoints, 10) || 0,
        dailyRedemptionLimit:
          Number.parseInt(tier.dailyRedemptionLimit, 10) || 0,
        sortOrder: idx + 1,
        badgeIcon: `/badges/${tier.name.toLowerCase()}.png`,
        colorHex:
          ["#CD7F32", "#C0C0C0", "#FFD700", "#E5E4E2"][idx] ?? "#000000",
        metadata: {},
      })
    );

    return {
      year: yearNum,
      name: `Tiering ${yearNum}`,
      validFrom: `${yearNum}-01-01`,
      validUntil: `${yearNum}-12-31`,
      isActive: true,
      tiers,
    };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = buildWritePayload();

      if (isEditMode && id) {
        await UpdateTieringPeriodUseCase(id, payload);
      } else {
        await CreateTieringPeriodUseCase(payload);
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Failed to save tiering period:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to save tiering period. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate("/swarapoint/tiering-settings");
  };

  const handleCancel = () => {
    navigate("/swarapoint/tiering-settings");
  };

  // Generate year options from current year to next 10 years
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  const handleRetryLoadDetail = () => {
    if (id) {
      void loadDetail(id);
    }
  };

  const handleGoBack = () => {
    navigate("/swarapoint/tiering-settings");
  };

  if (isLoadingDetail) {
    return (
      <MainLayout
        title={
          isEditMode ? "Edit Tiering Settings" : "Add New Tiering Settings"
        }
        onBack={handleGoBack}
      >
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">Loading tiering period...</p>
        </div>
      </MainLayout>
    );
  }

  if (isEditMode && loadDetailError) {
    return (
      <MainLayout title="Edit Tiering Settings" onBack={handleGoBack}>
        <div className="flex flex-col items-center justify-center py-16 gap-6">
          <div className="flex flex-col items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-red-500"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h3 className="text-lg font-semibold text-slate-900">
              Unable to Load Tiering Period
            </h3>
            <p className="text-sm text-slate-500 text-center max-w-md">
              {loadDetailError}
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="md" onClick={handleGoBack}>
              Go Back
            </Button>
            <Button variant="primary" size="md" onClick={handleRetryLoadDetail}>
              Retry
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title={isEditMode ? "Edit Tiering Settings" : "Add New Tiering Settings"}
      onBack={() => navigate("/swarapoint/tiering-settings")}
    >
      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        {/* Period Section */}
        <div className="bg-white border border-[#D1D3DB] rounded-lg p-6">
          <h3 className="text-base font-bold text-slate-900 mb-4 text-left">
            Period
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="validity-period"
                className="text-sm font-semibold text-gray-800 text-left"
              >
                Validity Period (Year)
              </label>
              <Select
                id="validity-period"
                value={formData.period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                errorMessage={errors.period}
                options={[
                  { value: "", label: "Select Year", disabled: true },
                  ...yearOptions,
                ]}
              />
            </div>
          </div>
        </div>

        {/* Tier Sections */}
        {formData.tiers.map((tier, index) => (
          <div
            key={index}
            className="bg-white border border-[#D1D3DB] rounded-lg p-6"
          >
            <h3 className="text-base font-bold text-slate-900 mb-4 text-left">
              Tier {index + 1}
            </h3>
            <div className="grid gap-4">
              {/* Tier Name and Points Range in 4-column grid */}
              <div className="grid grid-cols-4 gap-4">
                {/* Tier Name - 2 columns */}
                <div className="col-span-2 flex flex-col gap-1.5">
                  <label
                    htmlFor={`tier-${index}-name`}
                    className="text-sm font-semibold text-gray-800 text-left"
                  >
                    Tier Name
                  </label>
                  <Input
                    id={`tier-${index}-name`}
                    type="text"
                    value={tier.name}
                    onChange={(e) =>
                      handleTierChange(index, "name", e.target.value)
                    }
                    placeholder="Enter tier name"
                    errorMessage={errors[`tier${index}_name`]}
                    className="w-full"
                  />
                </div>

                {/* Points Range From - 1 column */}
                <div className="col-span-1 flex flex-col gap-1.5">
                  <label
                    htmlFor={`tier-${index}-points-from`}
                    className="text-sm font-semibold text-gray-800 text-left"
                  >
                    Points Range
                  </label>
                  <Input
                    id={`tier-${index}-points-from`}
                    type="number"
                    value={tier.pointsRangeFrom}
                    onChange={(e) =>
                      handleTierChange(index, "pointsRangeFrom", e.target.value)
                    }
                    placeholder="0"
                    errorMessage={errors[`tier${index}_pointsRangeFrom`]}
                  />
                </div>

                {/* Points Range To - 1 column */}
                <div className="col-span-1 flex flex-col gap-1.5">
                  <label
                    htmlFor={`tier-${index}-points-to`}
                    className="text-sm font-semibold text-gray-800 text-left invisible"
                  >
                    To
                  </label>
                  <Input
                    id={`tier-${index}-points-to`}
                    type="number"
                    value={tier.pointsRangeTo}
                    onChange={(e) =>
                      handleTierChange(index, "pointsRangeTo", e.target.value)
                    }
                    placeholder="100"
                    errorMessage={errors[`tier${index}_pointsRangeTo`]}
                  />
                </div>
              </div>

              {/* Benefits */}
              <div>
                <h4 className="text-base font-bold text-gray-800 mb-3 text-left">
                  Benefits
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor={`tier-${index}-direct-points`}
                        className="text-sm font-semibold text-gray-800 text-left"
                      >
                        Direct Points
                      </label>
                      <Input
                        id={`tier-${index}-direct-points`}
                        type="number"
                        value={tier.directPoints}
                        onChange={(e) =>
                          handleTierChange(
                            index,
                            "directPoints",
                            e.target.value
                          )
                        }
                        placeholder="0"
                        errorMessage={errors[`tier${index}_directPoints`]}
                        className="w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label
                        htmlFor={`tier-${index}-daily-limit`}
                        className="text-sm font-semibold text-gray-800 text-left"
                      >
                        Daily Redemption Limit
                      </label>
                      <Input
                        id={`tier-${index}-daily-limit`}
                        type="number"
                        value={tier.dailyRedemptionLimit}
                        onChange={(e) =>
                          handleTierChange(
                            index,
                            "dailyRedemptionLimit",
                            e.target.value
                          )
                        }
                        placeholder="0"
                        errorMessage={
                          errors[`tier${index}_dailyRedemptionLimit`]
                        }
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={`tier-${index}-birthday`}
                      className="text-sm font-semibold text-gray-800 text-left"
                    >
                      Birthday Reward Points
                    </label>
                    <Input
                      id={`tier-${index}-birthday`}
                      type="number"
                      value={tier.birthdayRewardPoints}
                      onChange={(e) =>
                        handleTierChange(
                          index,
                          "birthdayRewardPoints",
                          e.target.value
                        )
                      }
                      placeholder="0"
                      errorMessage={errors[`tier${index}_birthdayRewardPoints`]}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {submitError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {submitError}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between gap-3">
          <Button
            variant="outline"
            size="md"
            htmlType="button"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            htmlType="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Submit"}
          </Button>
        </div>
      </form>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Success"
        message={
          isEditMode
            ? "Tiering settings have been edited."
            : "New tiering settings has been created."
        }
      />
    </MainLayout>
  );
};
